import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaFlask, FaSyncAlt, FaPlay, FaCheckCircle, FaTimesCircle, FaQuestionCircle, FaVideo, FaEdit } from 'react-icons/fa';
import RecipeStudio from './RecipeStudio';
import './CarrierRecipes.css';

// Portal recipes of a carrier and their shadow-mode record (backend plan C3):
// the declarative recipe replayed next to the Selenium script, never
// submitting, so the agreement rate and locator drift are visible before a
// recipe is published. A test run stops before the submit step.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const pct = (v) => (typeof v === 'number' ? `${Math.round(v * 100)} %` : '—');

const CarrierRecipes = ({ carrierName, isAdmin = true }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [recipes, setRecipes] = useState(null);
    const [runnerAvailable, setRunnerAvailable] = useState(null);
    const [shadow, setShadow] = useState(null);
    const [error, setError] = useState(null);
    const [testing, setTesting] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [tracking, setTracking] = useState('');
    const [studio, setStudio] = useState(null); // null | { recipe } (recipe null = record a new one)

    const load = useCallback(async () => {
        if (!carrierName) return;
        setError(null);
        try {
            const [recipesRes, shadowRes] = await Promise.all([
                axios.get(`/api/logistics/carrier-recipes?carrier=${encodeURIComponent(carrierName)}`, authHeaders()),
                axios.get(`/api/logistics/carrier-recipes/shadow-runs?carrier=${encodeURIComponent(carrierName)}&limit=20`, authHeaders()),
            ]);
            setRecipes(recipesRes.data.recipes || []);
            setRunnerAvailable(recipesRes.data.runner_available);
            setShadow(shadowRes.data);
        } catch (e) {
            setRecipes([]);
            setShadow(null);
            setError(t('carrierRecipes.loadError', 'Impossible de charger les recettes.'));
        }
    }, [carrierName, t]);

    useEffect(() => { load(); }, [load]);

    const testRun = async (recipe) => {
        setTesting(recipe.id);
        setTestResult(null);
        setError(null);
        try {
            const variables = tracking ? { tracking_number: tracking.trim() } : {};
            const response = await axios.post(`/api/logistics/carrier-recipes/${encodeURIComponent(recipe.id)}/test-run`, { variables }, authHeaders());
            setTestResult({ recipe: recipe.id, ...response.data });
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('carrierRecipes.testError', 'La run de test a échoué.'));
        } finally {
            setTesting(null);
        }
    };

    const pairs = shadow?.summary?.pairs || [];
    const runs = shadow?.runs || [];
    const enabled = (shadow?.enabled_for || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const shadowOn = enabled.includes('*') || enabled.includes((carrierName || '').toLowerCase());

    // The studio (plan C5) records a new recipe even when the carrier has none yet.

    return (
        <section className="cd-card cd-recipes-card">
            <div className="cd-card-title">
                <h2><FaFlask /> {t('carrierRecipes.title', 'Recettes de portail')}</h2>
                <span className="cd-card-title-meta">
                    {runnerAvailable === false && <span className="crc-flag crc-flag-warn">{t('carrierRecipes.runnerUnavailable', 'runner indisponible')}</span>}
                    <span className={`crc-flag ${shadowOn ? 'crc-flag-ok' : ''}`}>{shadowOn ? t('carrierRecipes.shadowOn', 'mode ombre actif') : t('carrierRecipes.shadowOff', 'mode ombre inactif')}</span>
                    {isAdmin && <button type="button" className="u-btn u-btn--secondary" onClick={() => setStudio({ recipe: null })}><FaVideo /> {t('carrierRecipes.record', 'Enregistrer une recette')}</button>}
                    <button type="button" className="u-btn u-btn--secondary" onClick={load} title={t('carrierRecipes.refresh', 'Actualiser')}><FaSyncAlt /></button>
                </span>
            </div>
            <p className="crc-intro">{t('carrierRecipes.intro', 'Une recette décrit les étapes du portail par cibles sémantiques ; en mode ombre elle est rejouée à côté du script sans jamais soumettre, et l’accord entre les deux est mesuré avant publication.')}</p>
            {error && <div className="cd-alert cd-alert-error">{error}</div>}
            {recipes === null && <div className="crc-muted">{t('carrierRecipes.loading', 'Chargement…')}</div>}
            {recipes && recipes.length > 0 && (
                <div className="crc-table-wrap">
                    <table className="crc-table">
                        <thead>
                            <tr>
                                <th>{t('carrierRecipes.recipe', 'Recette')}</th>
                                <th>{t('carrierRecipes.operation', 'Opération')}</th>
                                <th>{t('carrierRecipes.status', 'Statut')}</th>
                                <th>{t('carrierRecipes.agreement', 'Accord (14 j)')}</th>
                                <th>{t('carrierRecipes.drift', 'Dérive')}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {recipes.filter((r) => r.operation !== 'login').map((r) => {
                                const pair = pairs.find((p) => p.operation === r.operation);
                                return (
                                    <tr key={`${r.source}-${r.db_id || r.id}`}>
                                        <td><strong>{r.id}</strong> <span className="crc-muted">v{r.version} · {r.scope === 'tenant' ? t('carrierRecipes.scopeTenant', 'compte') : t('carrierRecipes.scopeCatalog', 'catalogue')}</span></td>
                                        <td>{t(`carrierRecipes.op.${r.operation}`, r.operation)}</td>
                                        <td><span className={`crc-pill crc-pill-${r.status}`}>{t(`carrierRecipes.state.${r.status}`, r.status)}</span></td>
                                        <td>{pair ? `${pct(pair.agreement_rate)} (${pair.agreed}/${pair.agreed + pair.disagreed})` : '—'}</td>
                                        <td>{pair ? `${pair.drift_runs}/${pair.runs}` : (r.locator_cache_size ? `${r.locator_cache_size} ${t('carrierRecipes.learned', 'appris')}` : '—')}</td>
                                        <td>
                                            {isAdmin && r.source === 'db' && (
                                                <button type="button" className="u-btn u-btn--secondary crc-btn" onClick={() => setStudio({ recipe: r })} title={t('carrierRecipes.openStudio', 'Ouvrir dans le studio')}><FaEdit /></button>
                                            )}
                                            {isAdmin && r.operation !== 'login' && (
                                                <button type="button" className="u-btn u-btn--secondary crc-btn" disabled={testing === r.id || runnerAvailable === false} onClick={() => testRun(r)}>
                                                    <FaPlay /> {testing === r.id ? t('carrierRecipes.testing', 'Test en cours…') : t('carrierRecipes.testRun', 'Run de test')}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {isAdmin && recipes && recipes.length > 0 && (
                <div className="crc-test-input">
                    <label>{t('carrierRecipes.trackingForTest', 'Numéro de suivi pour la run de test')}</label>
                    <input className="cd-input" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="6A…" />
                </div>
            )}
            {testResult && testResult.run && (
                <div className={`crc-result crc-result-${testResult.run.status}`}>
                    <div className="crc-result-head">
                        <strong>{t(`carrierRecipes.runStatus.${testResult.run.status}`, testResult.run.status)}</strong>
                        <span className="crc-muted"> · {testResult.run.reached_step} · {Math.round((testResult.run.duration_ms || 0) / 1000)} s</span>
                        {testResult.run.error && <div className="cd-alert cd-alert-error">{testResult.run.error}</div>}
                    </div>
                    <div className="crc-steps">
                        {(testResult.run.steps || []).map((s) => (
                            <span key={s.id} className={`crc-step crc-step-${s.status}`} title={[s.strategy, s.selector, s.error].filter(Boolean).join(' · ')}>
                                {s.status === 'ok' ? <FaCheckCircle /> : s.status === 'failed' ? <FaTimesCircle /> : <FaQuestionCircle />} {s.id}{s.strategy === 'llm_repair' ? ' ✱' : ''}
                            </span>
                        ))}
                    </div>
                    {testResult.run.drift && testResult.run.drift.length > 0 && (
                        <div className="crc-muted">{t('carrierRecipes.driftNote', 'Localisateurs réparés par le modèle : {{steps}}', { steps: testResult.run.drift.join(', ') })}</div>
                    )}
                    {(testResult.captures || []).length > 0 && (
                        <div className="crc-captures">
                            {testResult.captures.slice(0, 8).map((c, i) => c.url ? <a key={i} href={c.url} target="_blank" rel="noopener noreferrer">{c.step || `capture ${i + 1}`}</a> : null)}
                        </div>
                    )}
                </div>
            )}
            {recipes && recipes.length === 0 && !error && <div className="crc-muted">{t('carrierRecipes.empty', 'Aucune recette pour ce transporteur : enregistrez-en une depuis le studio.')}</div>}
            {studio && <RecipeStudio carrierName={carrierName} initialRecipe={studio.recipe} onClose={() => setStudio(null)} onSaved={load} />}
            {runs.length > 0 && (
                <details className="crc-runs">
                    <summary>{t('carrierRecipes.recentRuns', 'Dernières runs ombre ({{count}})', { count: runs.length })}</summary>
                    <table className="crc-table">
                        <thead><tr><th>{t('carrierRecipes.when', 'Quand')}</th><th>{t('carrierRecipes.operation', 'Opération')}</th><th>{t('carrierRecipes.script', 'Script')}</th><th>{t('carrierRecipes.recipe', 'Recette')}</th><th>{t('carrierRecipes.verdict', 'Verdict')}</th></tr></thead>
                        <tbody>
                            {runs.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.created_at ? new Date(r.created_at).toLocaleString(lang) : ''}</td>
                                    <td>{t(`carrierRecipes.op.${r.operation}`, r.operation)}</td>
                                    <td>{r.selenium?.status}{r.selenium?.failure_category ? ` (${r.selenium.failure_category})` : ''}</td>
                                    <td>{t(`carrierRecipes.runStatus.${r.recipe?.status}`, r.recipe?.status)}{r.recipe?.reached_step ? ` · ${r.recipe.reached_step}` : ''}{(r.recipe?.drift || []).length ? ' ✱' : ''}</td>
                                    <td className={r.agreement === true ? 'crc-ok' : r.agreement === false ? 'crc-bad' : ''}>{r.agreement === true ? '✓' : r.agreement === false ? '✗' : '?'} <span className="crc-muted">{t(`carrierRecipes.reason.${r.reason}`, (r.reason || '').replace(/_/g, ' '))}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}
        </section>
    );
};

export default CarrierRecipes;
